import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function Logo({ className = "", onDark = false }) {
  return (
    <Link to="/" className={`flex items-center gap-2 ${className}`} data-testid="brand-logo">
      <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm shadow-blue-600/30">
        <ShieldCheck className="w-5 h-5 text-white" />
      </div>
      <div className="leading-none">
        <span className={`font-heading font-extrabold text-xl tracking-tight ${onDark ? "text-white" : "text-slate-900"}`}>
          TrustFlow
        </span>
      </div>
    </Link>
  );
}

export function StatCard({ label, value, sub, tone = "default", mono = true, testid }) {
  const tones = {
    default: "bg-white border-slate-200",
    blue: "bg-blue-50 border-blue-200",
    green: "bg-emerald-50 border-emerald-200",
    amber: "bg-amber-50 border-amber-200",
    rose: "bg-rose-50 border-rose-200",
  };
  const valTones = {
    default: "text-slate-900", blue: "text-blue-700", green: "text-emerald-700",
    amber: "text-amber-700", rose: "text-rose-700",
  };
  return (
    <div className={`rounded-2xl border p-5 sm:p-6 shadow-sm ${tones[tone]}`} data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-2 text-2xl sm:text-3xl font-bold ${mono ? "font-mono" : "font-heading"} ${valTones[tone]}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-sm text-slate-500">{sub}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  "Pending": "bg-slate-100 text-slate-700",
  "Pending Verification": "bg-amber-100 text-amber-800",
  "Under Review": "bg-amber-100 text-amber-800",
  "Payment Processing": "bg-blue-100 text-blue-800",
  "Partially Paid": "bg-indigo-100 text-indigo-800",
  "Pending Settlement": "bg-indigo-100 text-indigo-800",
  "Payment Sent": "bg-blue-100 text-blue-800",
  "Confirmation Pending": "bg-blue-100 text-blue-800",
  "Payment Dispute / Needs Review": "bg-rose-100 text-rose-800",
  "Confirmation Expired": "bg-rose-100 text-rose-800",
  "Completed": "bg-emerald-100 text-emerald-800",
  "Approved": "bg-emerald-100 text-emerald-800",
  "Rejected": "bg-rose-100 text-rose-800",
  "rejected": "bg-rose-100 text-rose-800",
  "Cancelled": "bg-slate-200 text-slate-600",
  "Partially Settled — Cancelled": "bg-amber-100 text-amber-800",
  "Cancelling": "bg-amber-100 text-amber-800",
  "payment_sent": "bg-blue-100 text-blue-800",
  "customer_confirmed": "bg-emerald-100 text-emerald-800",
  "dispute": "bg-rose-100 text-rose-800",
  "expired": "bg-rose-100 text-rose-800",
  "Open": "bg-amber-100 text-amber-800",
  "In Review": "bg-blue-100 text-blue-800",
  "Resolved": "bg-emerald-100 text-emerald-800",
  "Closed": "bg-slate-200 text-slate-600",
  "active": "bg-emerald-100 text-emerald-800",
  "temporarily_blocked": "bg-amber-100 text-amber-800",
  "suspended": "bg-rose-100 text-rose-800",
  "deactivated": "bg-slate-200 text-slate-600",
  "pending": "bg-amber-100 text-amber-800",
};

export function StatusBadge({ status, testid }) {
  const live = ["Pending", "Under Review", "payment_sent", "Payment Sent", "Open", "Pending Verification"].includes(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || "bg-slate-100 text-slate-700"}`} data-testid={testid}>
      {live && <span className="w-1.5 h-1.5 rounded-full bg-current pulse-dot" />}
      {status}
    </span>
  );
}

export function CopyButton({ value, testid, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const t = document.createElement("textarea");
      t.value = value; document.body.appendChild(t); t.select();
      document.execCommand("copy"); document.body.removeChild(t);
    }
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {copied ? "Copied" : label}
    </button>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }

export function Countdown({ expiry, onExpire, testid }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.floor((new Date(expiry).getTime() - Date.now()) / 1000)));
  const fired = useRef(false);
  useEffect(() => {
    const id = setInterval(() => {
      const r = Math.max(0, Math.floor((new Date(expiry).getTime() - Date.now()) / 1000));
      setRemaining(r);
      if (r <= 0 && !fired.current) { fired.current = true; onExpire && onExpire(); }
    }, 1000);
    return () => clearInterval(id);
  }, [expiry, onExpire]);
  const mm = Math.floor(remaining / 60), ss = remaining % 60;
  const danger = remaining < 120;
  const done = remaining <= 0;
  return (
    <span data-testid={testid}
      className={`font-mono font-bold text-xl tabular-nums ${done ? "text-rose-600" : danger ? "text-rose-600 pulse-dot" : "text-amber-600"}`}>
      {done ? "00:00" : `${pad(mm)}:${pad(ss)}`}
    </span>
  );
}

export function TelegramButton({ url, className = "", testid = "telegram-support-button" }) {
  const href = url || "https://t.me/shroud789";
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#229ED9] text-white text-sm font-semibold hover:brightness-110 transition ${className}`}>
      <Send className="w-4 h-4" />
      Telegram Support
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 pulse-dot" />
    </a>
  );
}

export function AuthImage({ fileId, className = "", alt = "evidence" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (!fileId) return;
    api.get(`/files/${fileId}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [fileId]);
  if (!fileId) return null;
  if (!src) return <div className={`bg-slate-100 animate-pulse rounded-lg ${className}`} />;
  return <img src={src} alt={alt} className={className} />;
}

export function ZoomImage({ fileId, className = "", alt = "image", placeholder = "No image provided", testid }) {
  const [open, setOpen] = useState(false);
  if (!fileId) {
    return <div className={`flex items-center justify-center text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-300 rounded-xl ${className}`} data-testid={testid ? `${testid}-placeholder` : undefined}>{placeholder}</div>;
  }
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} className="block group relative">
        <AuthImage fileId={fileId} className={className} alt={alt} />
        <span className="absolute bottom-2 right-2 text-xs bg-black/60 text-white px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition">View full image</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={testid ? `${testid}-fullscreen` : undefined}>
          <AuthImage fileId={fileId} className="max-w-full max-h-full object-contain rounded-lg" alt={alt} />
          <button className="absolute top-4 right-4 text-white/80 hover:text-white text-sm bg-white/10 px-3 py-1.5 rounded-lg">Close ✕</button>
        </div>
      )}
    </>
  );
}

export function usePoll(fn, interval = 6000, deps = []) {
  useEffect(() => {
    let active = true;
    const run = () => active && fn();
    run();
    const id = setInterval(run, interval);
    return () => { active = false; clearInterval(id); };
    // eslint-disable-next-line
  }, deps);
}

export function fmtUSDT(v) {
  const n = parseFloat(v || 0);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}
export function fmtINR(v) {
  const n = parseFloat(v || 0);
  return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
